import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, readJson } from "../../lib/api";
import { formatMoney } from "../../lib/engage";
import { useUnits } from "../../hooks/useUnits";
import { fmtRate, G_PER_LB, G_PER_OZ } from "../../lib/units";
import {
  breakEvenGph, goals, median, paceStats, simulate, simulateToday, solveRate, upliftToMatch,
  type PlanCrew, type Scheme, type SchemeResult,
} from "../../lib/payPlanner";

// Owner-only "what if" for pay: the crew's real logged pace over a window, run
// through hourly, piece rate and base + bonus schemes. Maths lives in lib/payPlanner.

type PlannerData = {
  currency: string; piece_rate_cents_per_g: number | null; days: number;
  crew: { player_id: number; name: string; hourly_cents: number | null; days: { day: string; hours: number; grams: number }[] }[];
};
type Key = "today" | "hourly" | "piece" | "hybrid";
const WINDOWS = [30, 90, 180] as const;

const num = (s: string) => (s.trim() === "" ? 0 : Number(s) || 0);
const pct = (x: number) => (Math.abs(x) < 0.0005 ? "0.0%" : `${x > 0 ? "+" : ""}${(x * 100).toFixed(1)}%`);

export function PayPlannerTab() {
  const units = useUnits();
  const imperial = units === "imperial";
  // Rates are entered per g (metric) or per lb (imperial); pace in the app's rate unit.
  const perW = imperial ? G_PER_LB : 1;
  const rateToCents = (s: string) => (num(s) * 100) / perW;
  const centsToRate = (c: number) => ((c * perW) / 100).toFixed(imperial ? 2 : 3);
  const paceToGph = (s: string) => (imperial ? num(s) * G_PER_OZ : num(s) * 60);
  const gphToPace = (g: number) => (imperial ? (g / G_PER_OZ).toFixed(1) : (g / 60).toFixed(2));
  const pace = (gph: number) => fmtRate(gph / 60, units, 2);
  const costUnit = imperial ? "lb" : "kg";
  const perCostUnit = (centsPerG: number) => centsPerG * (imperial ? G_PER_LB : 1000);

  const [days, setDays] = useState<number>(90);
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const { data, isLoading, isError } = useQuery({
    queryKey: ["pay-planner", days, tz],
    queryFn: () => api.get(`/api/payroll/planner?days=${days}&tz=${encodeURIComponent(tz)}`).then((r) => readJson<PlannerData>(r)),
  });
  const crew: PlanCrew[] = useMemo(
    () => (data?.crew ?? []).map((c) => ({ id: c.player_id, name: c.name, hourlyCents: c.hourly_cents, days: c.days })),
    [data],
  );
  const money = (cents: number) => formatMoney(Math.round(cents), data?.currency ?? "NZD");

  const [uplift, setUplift] = useState(0); // percent
  const [floor, setFloor] = useState("");
  const [wage, setWage] = useState("");
  const [piece, setPiece] = useState("");
  const [base, setBase] = useState("");
  const [bonus, setBonus] = useState("");
  const [target, setTarget] = useState("");
  const [selected, setSelected] = useState<Key>("piece");

  // Starting values once data arrives: today's median wage, a piece rate and a bonus
  // rate that each cost the same as paying everyone that wage, and a bonus threshold
  // at the crew's median pace. All of them editable.
  const [seeded, setSeeded] = useState(false);
  useEffect(() => {
    if (seeded || !crew.length) return;
    const rates = crew.map((c) => c.hourlyCents).filter((x): x is number => x != null && x > 0);
    const w = rates.length ? median(rates) : 2500;
    const ref = simulate(crew, { baseCents: w, rateCentsPerG: 0, targetGph: 0, floorCents: 0 });
    const medPace = median(crew.map((c) => paceStats(c).gph));
    const b = Math.round(w * 0.6);
    const pr = data?.piece_rate_cents_per_g ?? solveRate(crew, { baseCents: 0, rateCentsPerG: 0, targetGph: 0, floorCents: 0 }, 0, goals.sameCost(ref)) ?? 0;
    const br = solveRate(crew, { baseCents: b, rateCentsPerG: 0, targetGph: medPace, floorCents: 0 }, 0, goals.sameCost(ref)) ?? 0;
    setWage((w / 100).toFixed(2)); setPiece(centsToRate(pr)); setBase((b / 100).toFixed(2));
    setBonus(centsToRate(br)); setTarget(gphToPace(medPace)); setSeeded(true);
  }, [crew, seeded]); // eslint-disable-line react-hooks/exhaustive-deps

  const floorCents = num(floor) * 100;
  const u = uplift / 100;
  const schemes: Record<Exclude<Key, "today">, Scheme> = {
    hourly: { baseCents: num(wage) * 100, rateCentsPerG: 0, targetGph: 0, floorCents },
    piece: { baseCents: 0, rateCentsPerG: rateToCents(piece), targetGph: 0, floorCents },
    hybrid: { baseCents: num(base) * 100, rateCentsPerG: rateToCents(bonus), targetGph: paceToGph(target), floorCents },
  };
  const today = useMemo(() => simulateToday(crew, data?.piece_rate_cents_per_g ?? null), [crew, data]);
  const hasToday = today.cost > 0;
  const results: Partial<Record<Key, SchemeResult>> = {
    ...(hasToday ? { today } : {}),
    hourly: simulate(crew, schemes.hourly, u),
    piece: simulate(crew, schemes.piece, u),
    hybrid: simulate(crew, schemes.hybrid, u),
  };
  const ref = hasToday ? today : results.hourly!;
  const refName = hasToday ? "today" : "hourly";
  const cheapest = (Object.keys(results) as Key[]).reduce((a, b) => (results[b]!.costPerGram < results[a]!.costPerGram ? b : a));

  function solve(key: "piece" | "hybrid", goal: "sameCost" | "nobodyWorseOff") {
    const r = solveRate(crew, schemes[key], u, goals[goal](ref));
    if (r == null) return;
    // Round up to the shown precision so the goal still holds after rounding.
    const step = imperial ? 0.01 : 0.001;
    (key === "piece" ? setPiece : setBonus)((Math.ceil((r * perW) / 100 / step - 1e-9) * step).toFixed(imperial ? 2 : 3));
  }

  if (isLoading) return <div className="panel muted small">Loading…</div>;
  if (isError) return <div className="panel muted small">The pay planner couldn&rsquo;t load. Try again in a moment.</div>;
  if (!crew.length) {
    return <div className="panel"><h2>Pay planner</h2><div className="muted small">No finished runs with a weight in the last {days} days yet. Log some work and come back.</div></div>;
  }

  const sel = results[selected] ?? results.piece!;
  const insights = buildInsights();

  function buildInsights(): string[] {
    const out: string[] = [];
    const be = breakEvenGph(schemes.hourly.baseCents, schemes.piece.rateCentsPerG);
    if (be) {
      const fast = crew.filter((c) => paceStats(c).gph > be).map((c) => c.name);
      const slow = crew.filter((c) => paceStats(c).gph <= be).map((c) => c.name);
      out.push(`$${num(wage).toFixed(2)}/h and ${money(schemes.piece.rateCentsPerG * perW)} per ${imperial ? "lb" : "g"} cost the same for someone trimming ${pace(be)}. `
        + (fast.length ? `Piece rate costs you more for ${fast.join(", ")} (faster than that)` : "Nobody is faster than that, so piece rate costs you less for everyone")
        + (fast.length && slow.length ? ` and less for ${slow.join(", ")}.` : "."));
    }
    if (floorCents > 0) {
      for (const k of ["piece", "hybrid"] as const) {
        const r = results[k]!;
        if (r.topUp < 1) continue;
        const top = [...r.people].sort((a, b) => b.topUp - a.topUp)[0];
        const hidden = r.cost - r.averagedCost;
        out.push(`${k === "piece" ? "Piece rate" : "Base + bonus"}: the $${num(floor).toFixed(2)}/h minimum adds ${money(r.topUp)} in top-ups, mostly for ${top.name}.`
          + (hidden >= 1 ? ` ${money(hidden)} of it comes from slow days that averages hide, because the minimum is checked day by day.` : ""));
      }
    }
    for (const k of ["piece", "hybrid"] as const) {
      const need = upliftToMatch(crew, schemes[k], ref.costPerGram);
      const label = k === "piece" ? "Piece rate" : "Base + bonus";
      if (need === 0) out.push(`${label} already costs less per ${costUnit} than ${refName} at your crew's current pace.`);
      else if (need != null) out.push(`${label} matches ${refName}'s cost per ${costUnit} if pace rises ${(need * 100).toFixed(1)}%.`);
    }
    const swingy = crew.map((c) => ({ c, s: paceStats(c) })).filter((x) => x.s.spread > 0.35 && x.c.days.length >= 3);
    if (swingy.length) {
      out.push(`${swingy.map((x) => `${x.c.name} (±${Math.round(x.s.spread * 100)}%)`).join(", ")}: pace swings a lot day to day. Pay tied to output will swing with it, and a minimum guarantee costs more for them than their average suggests.`);
    }
    return out;
  }

  const card = (key: Key, title: string, inputs: ReactNode, solvable?: "piece" | "hybrid") => {
    const r = results[key];
    if (!r) return null;
    const worse = key === "today" ? 0 : r.people.filter((p, i) => p.pay < ref.people[i].pay - 0.5).length;
    const diff = ref.cost ? r.cost / ref.cost - 1 : 0;
    return (
      <div
        key={key} className={`panel${selected === key ? " active" : ""}`} data-testid={`plan-${key}`}
        style={{ flex: "1 1 220px", minWidth: 0, cursor: "pointer", outline: selected === key ? "2px solid var(--accent, #3b5bdb)" : undefined }}
        onClick={() => setSelected(key)}
      >
        <h2 style={{ fontSize: "1rem" }}>
          {title}
          {cheapest === key && <span className="pill live-pill" style={{ marginLeft: "auto" }}>Lowest per {costUnit}</span>}
        </h2>
        <div onClick={(e) => e.stopPropagation()}>{inputs}</div>
        <div style={{ fontSize: "1.4rem", fontWeight: 700, marginTop: 8 }} data-testid={`plan-${key}-per-unit`}>
          {money(perCostUnit(r.costPerGram))}<span className="muted small"> per {costUnit}</span>
        </div>
        <div className="small muted">
          {money(r.cost)} over {days} days
          {key !== refName && ref.cost > 0 && <> · <b>{pct(diff)}</b> vs {refName}</>}
        </div>
        {key !== "today" && (
          <div className="small muted">
            {worse ? `${worse} of ${r.people.length} earn less than ${refName}` : `Nobody earns less than ${refName}`}
            {r.topUp >= 1 && ` · top-ups ${money(r.topUp)}`}
          </div>
        )}
        {solvable && (
          <div className="row" style={{ gap: 6, flexWrap: "wrap", marginTop: 6 }} onClick={(e) => e.stopPropagation()}>
            <button type="button" className="btn btn-sm" onClick={() => solve(solvable, "sameCost")}>Match {refName}&rsquo;s cost</button>
            <button type="button" className="btn btn-sm" onClick={() => solve(solvable, "nobodyWorseOff")}>Nobody earns less</button>
          </div>
        )}
      </div>
    );
  };

  const field = (label: string, value: string, set: (s: string) => void, unit: string, testId: string) => (
    <label className="row small" style={{ gap: 6, alignItems: "center", marginBottom: 4 }}>
      <span style={{ minWidth: 70 }}>{label}</span>
      <input type="number" inputMode="decimal" min={0} step="any" value={value} data-testid={testId}
        onChange={(e) => set(e.target.value)} style={{ maxWidth: 90 }} />
      <span className="muted">{unit}</span>
    </label>
  );
  const rateUnit = `per ${imperial ? "lb" : "g"}`;

  return (
    <div className="panel" data-testid="pay-planner">
      <h2>Pay planner</h2>
      <div className="small muted" style={{ marginBottom: 10 }}>
        Uses each person&rsquo;s logged active trimming time and weight. Try different rates and see what each one would have cost
        over the same work, and who would have earned more or less.
      </div>

      <div className="row" style={{ gap: 12, flexWrap: "wrap", alignItems: "center", marginBottom: 12 }}>
        <div className="row" style={{ gap: 6, flexWrap: "wrap" }} role="group" aria-label="Period">
          {WINDOWS.map((d) => (
            <button key={d} type="button" className={`chip${days === d ? " active" : ""}`} aria-pressed={days === d} onClick={() => setDays(d)}>
              Last {d} days
            </button>
          ))}
        </div>
        <label className="row small" style={{ gap: 6, alignItems: "center" }}>
          Minimum $/h
          <input type="number" min={0} step="any" placeholder="none" value={floor} data-testid="plan-floor"
            onChange={(e) => setFloor(e.target.value)} style={{ maxWidth: 80 }} />
        </label>
        <label className="row small" style={{ gap: 6, alignItems: "center" }}>
          Pace change when paid by output
          <input type="range" min={-10} max={30} step={1} value={uplift} aria-label="Pace change" onChange={(e) => setUplift(Number(e.target.value))} />
          <b>{uplift > 0 ? "+" : ""}{uplift}%</b>
        </label>
      </div>
      <div className="small muted" style={{ marginBottom: 12 }}>
        Minimum: piece-rate workers usually still have to earn at least the minimum wage for their hours. It&rsquo;s checked per day worked here,
        which is the cautious reading. Pace change is your guess at how much faster people trim when pay depends on output. It&rsquo;s an
        assumption, not a measurement: start at 0%.
      </div>

      <div className="row" style={{ gap: 10, flexWrap: "wrap", alignItems: "stretch" }}>
        {card("today", "Today", <div className="small muted">Each person&rsquo;s hourly rate{data?.piece_rate_cents_per_g != null ? " + your piece rate" : ""}</div>)}
        {card("hourly", "Hourly", field("Wage", wage, setWage, "per hour", "plan-wage"))}
        {card("piece", "Piece rate", field("Rate", piece, setPiece, rateUnit, "plan-piece-rate"), "piece")}
        {card("hybrid", "Base + bonus", <>
          {field("Base", base, setBase, "per hour", "plan-base")}
          {field("Bonus", bonus, setBonus, rateUnit, "plan-bonus")}
          {field("Above", target, setTarget, imperial ? "oz/hr" : "g/min", "plan-target")}
        </>, "hybrid")}
      </div>

      {insights.length > 0 && (
        <div className="panel" style={{ marginTop: 12 }} data-testid="plan-insights">
          <h2 style={{ fontSize: "1rem" }}>What the numbers say</h2>
          <ul style={{ margin: 0, paddingLeft: 18 }}>{insights.map((t) => <li key={t} className="small" style={{ marginBottom: 6 }}>{t}</li>)}</ul>
        </div>
      )}

      <div className="table-wrap" style={{ marginTop: 12 }}>
        <table className="data-table" data-testid="plan-crew">
          <thead>
            <tr>
              <th>Trimmer</th><th>Pace</th><th>Hours</th>
              <th>{refName === "today" ? "Today" : "Hourly"} $/h</th>
              <th>{selected === "today" ? "Today" : selected === "hourly" ? "Hourly" : selected === "piece" ? "Piece rate" : "Base + bonus"} $/h</th>
              <th>Change</th><th>Top-up days</th>
            </tr>
          </thead>
          <tbody>
            {sel.people.map((p, i) => {
              const s = paceStats(crew[i]);
              const r0 = ref.people[i];
              const change = r0.pay ? p.pay / r0.pay - 1 : 0;
              return (
                <tr key={p.id}>
                  <td>{p.name}</td>
                  <td>{pace(s.gph)}{s.spread > 0 && <span className="muted"> ±{Math.round(s.spread * 100)}%</span>}</td>
                  <td>{p.hours.toFixed(1)}</td>
                  <td>{money(r0.perHour)}</td>
                  <td>{money(p.perHour)}</td>
                  <td>{pct(change)}</td>
                  <td>{p.floorDays || "–"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
